import { FormHeaderAndContent, FormPageContainer } from '@strata/react-form-header-and-content';
import { FormAccordion } from '@strata/react-form-section';
import { FullscreenLoading } from '@strata/react-fullscreen-loading';
import { Formik, getIn } from 'formik';
import { useState } from 'react';

import { ConfirmPatientDialog } from 'components/shared/ConfirmPatientDialog';
import { PatientAndPharmacySection } from 'components/tools/testReferral/PatientAndPharmacySection';
import { PatientDetailsHeader } from 'components/tools/testReferral/PatientDetailsHeader';
import { ProductSection } from 'components/tools/testReferral/ProductSection';
import { ResultsSection } from 'components/tools/testReferral/ResultsSection';
import { TestReferralHeader } from 'components/tools/testReferral/TestReferralHeader';
import {
    Sections,
    SectionToStepMap,
    StepToSectionMap,
    TestReferralReduxListener,
    validationSchema,
} from 'services/testReferralHelpers';
import { useTestReferralData } from 'store/testReferral/testReferralSlice';

const initialValues = {
    patientAndPharmacy: {
        includePendStart: false,
        pharmacy: undefined,
        healthPlan: undefined,
        patientFirstName: undefined,
        patientLastName: undefined,
        cardholderId: undefined,
        eligibility: undefined,
        placeOfService: {
            value: 'HOME',
            name: 'Patient Home',
            secondaryData: 'HOME',
        },
    },
    product: undefined,
};

export const TestReferral = () => {
    const {
        showPatientHeader,
        setShowPatientHeader,
        openSection,
        setOpenSection,
        testReferralData,
    } = useTestReferralData();

    const [loadingText, setLoadingText] = useState();
    const [showPatientDialog, setShowPatientDialog] = useState(false);

    return (
        <FormPageContainer>
            <Formik
                initialValues={testReferralData?.values ?? initialValues}
                initialTouched={testReferralData?.touched}
                validationSchema={validationSchema}
                validateOnMount
                enableReinitialize
            >
                {(formik) => {
                    const getSectionStatus = (sectionId) => {
                        const sectionTouched = !!getIn(formik.touched, sectionId);
                        if (sectionTouched && !getIn(formik.errors, sectionId)) {
                            return FormAccordion.statuses.COMPLETED;
                        }
                        if (sectionTouched) {
                            return FormAccordion.statuses.ACTIVE;
                        }
                        return '';
                    };

                    const isSectionComplete = (status) =>
                        status === FormAccordion.statuses.COMPLETED;

                    const handleSectionClick = (step) => () => {
                        const previousSections = [...Array(step - 1)].map((_v, i) =>
                            getSectionStatus(StepToSectionMap[i + 1]),
                        );
                        if (openSection !== step && previousSections.every(isSectionComplete)) {
                            setOpenSection(step);
                        }
                    };

                    const getSectionProps = (sectionId) => {
                        const step = SectionToStepMap[sectionId];

                        return {
                            step,
                            progressIndicatorStatus: getSectionStatus(sectionId),
                            onSectionClick: handleSectionClick(step),
                            isOpen: openSection === step,
                        };
                    };

                    const resetProductAndResults = async () => {
                        if (
                            getIn(
                                formik.values,
                                `${Sections.PATIENT_AND_PHARMACY}.eligibility.eligibilityConfirmed`,
                            )
                        ) {
                            await formik.setFieldValue(Sections.PRODUCT, undefined);
                            await formik.setFieldValue(Sections.RESULTS, undefined);
                            const { patientAndPharmacy: patientAndPharmacyTouched } =
                                formik.touched;
                            formik.setTouched({ patientAndPharmacy: patientAndPharmacyTouched });
                            setShowPatientHeader(false);
                        }
                    };

                    const onClear = () => {
                        formik.resetForm({ values: initialValues, touched: {} });
                        setOpenSection(SectionToStepMap[Sections.PATIENT_AND_PHARMACY]);
                        setShowPatientHeader(false);
                    };

                    const sectionDataArray = [
                        {
                            sectionTitle: 'Select Pharmacy & Patient',
                            content: (
                                <PatientAndPharmacySection
                                    resetProductAndResults={resetProductAndResults}
                                    setShowPatientDialog={setShowPatientDialog}
                                    setLoadingText={setLoadingText}
                                />
                            ),
                            ...getSectionProps(Sections.PATIENT_AND_PHARMACY),
                            displayFooter: false,
                            scrollToTopOnOpen: true,
                            disabled: false,
                        },
                        {
                            sectionTitle: 'Select Product',
                            content: <ProductSection setLoadingText={setLoadingText} />,
                            ...getSectionProps(Sections.PRODUCT),
                            displayFooter: false,
                            scrollToTopOnOpen: true,
                            disabled: false,
                        },
                        {
                            sectionTitle: 'Test Referral Results',
                            content: <ResultsSection onClear={onClear} />,
                            ...getSectionProps(Sections.RESULTS),
                            displayFooter: false,
                            scrollToTopOnOpen: true,
                            disabled: false,
                        },
                    ];

                    return (
                        <>
                            <FullscreenLoading
                                isShowing={!!loadingText}
                                loadingText={loadingText ?? ''}
                            />
                            <TestReferralReduxListener />
                            <ConfirmPatientDialog
                                isOpen={showPatientDialog}
                                patientDetails={
                                    formik.values[Sections.PATIENT_AND_PHARMACY]?.eligibility
                                }
                                onConfirm={async () => {
                                    setShowPatientDialog(false);
                                    await formik.setFieldValue(
                                        `${Sections.PATIENT_AND_PHARMACY}.eligibility.eligibilityConfirmed`,
                                        true,
                                    );
                                    setOpenSection(SectionToStepMap[Sections.PRODUCT]);
                                    setShowPatientHeader(true);
                                }}
                                onReturnToForm={() => setShowPatientDialog(false)}
                            />
                            <div className="sds_test-referral">
                                <FormHeaderAndContent
                                    rowOneContentHeader={<TestReferralHeader onClear={onClear} />}
                                    rowThreeContentHeader={
                                        showPatientHeader && (
                                            <PatientDetailsHeader
                                                patientDetails={getIn(
                                                    formik.values,
                                                    Sections.PATIENT_AND_PHARMACY,
                                                )}
                                            />
                                        )
                                    }
                                    mainContent={
                                        <FormAccordion sectionDataArray={sectionDataArray} />
                                    }
                                />
                            </div>
                        </>
                    );
                }}
            </Formik>
        </FormPageContainer>
    );
};
